import { useState } from 'react';
import { createPasswordClient, type PasswordMode } from '@orbit-mind/api-client';
import { useLocale, getLocale, passwordErrorMessage } from '@orbit-mind/engine-react';
import type { SignInConsent } from '@orbit-mind/shared-types';
import { Button } from '../ui/controls';

export function EmailPasswordForm({
  agreed,
  consent,
  onSignedIn,
  requestConsent,
  initialEmail = '',
  initialMode,
}: {
  readonly agreed: boolean;
  readonly consent: SignInConsent;
  readonly onSignedIn: () => void;
  readonly requestConsent?: (continueAfterConsent: (choice: SignInConsent) => void) => void;
  readonly initialEmail?: string;
  readonly initialMode?: 'login' | 'register';
}): React.JSX.Element {
  const { text: t } = useLocale();
  const [mode, setMode] = useState<PasswordMode>(
    initialMode ?? (initialEmail ? 'register' : 'login'),
  );
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const proof = mode === 'verify' || mode === 'reset';
  const hasPassword = mode === 'login' || mode === 'register' || mode === 'reset';
  const switchMode = (value: PasswordMode): void => {
    setMode(value);
    setPassword('');
    setConfirmation('');
    setCode('');
    setNotice('');
    setVisible(false);
  };
  const submit = async (confirmed = false, action = mode, choice = consent): Promise<void> => {
    if (busy) return;
    if (action === 'register' && !agreed && !confirmed) {
      requestConsent?.((accepted) => {
        void submit(true, action, accepted);
      });
      return;
    }
    if (
      (mode === 'register' || mode === 'reset') &&
      (password.length < 12 || password !== confirmation)
    ) {
      setNotice(
        t(
          'Use at least 12 characters and matching passwords.',
          'Use pelo menos 12 caracteres e senhas iguais.',
        ),
      );
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      const result = await createPasswordClient(
        { fetch: (input, init) => globalThis.fetch(input, init) },
        'web',
      )(action, {
        email,
        password,
        code,
        consent: choice,
        locale: getLocale().startsWith('pt') ? 'pt-BR' : 'en',
      });
      if (!result.ok) {
        setNotice(passwordErrorMessage(result.error, action, t));
        return;
      }
      if (action === 'login') {
        setPassword('');
        onSignedIn();
      } else if (action === 'register' || action === 'forgot' || action === 'resend') {
        setMode(
          result.value.delivery === 'link' ? 'login' : action === 'register' ? 'verify' : 'reset',
        );
        setPassword('');
        setConfirmation('');
        setNotice(
          result.value.delivery === 'link'
            ? t(
                'Check your email and spam folder. Open the link to confirm your email or reset your password, then return here to sign in. If your email is already confirmed, you can sign in now.',
                'Confira seu e-mail e a pasta de spam. Abra o link para confirmar o e-mail ou redefinir a senha e volte para entrar. Se o e-mail já está confirmado, você já pode entrar.',
              )
            : t(
                'If eligible, a code was sent to your email. Check spam too. It expires in 15 minutes.',
                'Se elegível, um código foi enviado ao seu e-mail. Confira também o spam. Ele expira em 15 minutos.',
              ),
        );
      } else {
        switchMode('login');
        setNotice(
          t(
            'Email confirmed. Sign in with your password.',
            'E-mail confirmado. Entre com sua senha.',
          ),
        );
      }
    } finally {
      setBusy(false);
    }
  };
  const field = 'border-edge bg-surface text-ink min-h-11 rounded-lg border px-3 text-sm';
  return (
    <form
      className="grid gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {mode === 'login' && (
        <div className="border-edge grid gap-2 rounded-lg border p-3">
          <p className="text-ink-muted text-sm">
            {t(
              'New here? Create your account first.',
              'Primeiro acesso? Crie sua conta antes de entrar.',
            )}
          </p>
          <Button
            type="button"
            disabled={busy}
            onClick={() => {
              switchMode('register');
            }}
          >
            {t('Create account / Add a password', 'Criar conta / Adicionar senha')}
          </Button>
        </div>
      )}
      <h3 className="text-sm font-semibold">
        {mode === 'login'
          ? t('Sign in with email', 'Entrar com e-mail')
          : mode === 'register'
            ? t('Create an email account', 'Criar conta com e-mail')
            : mode === 'forgot'
              ? t('Recover password', 'Recuperar senha')
              : t('Confirm your email', 'Confirme seu e-mail')}
      </h3>
      <label className="grid gap-1 text-sm">
        {t('Email address', 'E-mail')}
        <input
          type="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          disabled={busy || proof || Boolean(initialEmail)}
          autoComplete="email"
          maxLength={254}
          className={field}
        />
      </label>
      {proof && (
        <label className="grid gap-1 text-sm">
          {t('Email code', 'Código do e-mail')}
          <input
            required
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
            disabled={busy}
            inputMode="numeric"
            pattern="[0-9]{8}"
            autoComplete="one-time-code"
            maxLength={8}
            className={field}
          />
        </label>
      )}
      {hasPassword && (
        <>
          <label className="grid gap-1 text-sm">
            {t('Password', 'Senha')}
            <input
              required
              type={visible ? 'text' : 'password'}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              disabled={busy}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              maxLength={128}
              className={field}
            />
          </label>
          <Button
            type="button"
            onClick={() => {
              setVisible(!visible);
            }}
          >
            {visible ? t('Hide password', 'Ocultar senha') : t('Show password', 'Mostrar senha')}
          </Button>
        </>
      )}
      {(mode === 'register' || mode === 'reset') && (
        <>
          <p className="text-ink-muted text-xs">
            {t(
              'Use 12–128 characters. Spaces and password managers are welcome.',
              'Use 12–128 caracteres. Espaços e gerenciadores de senha são aceitos.',
            )}
          </p>
          <label className="grid gap-1 text-sm">
            {t('Confirm password', 'Confirmar senha')}
            <input
              required
              type={visible ? 'text' : 'password'}
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
              }}
              disabled={busy}
              autoComplete="new-password"
              maxLength={128}
              className={field}
            />
          </label>
        </>
      )}
      {notice && (
        <p role="alert" className="text-sm">
          {notice}
        </p>
      )}
      <Button
        type="submit"
        tone="primary"
        disabled={
          busy ||
          !email.trim() ||
          (mode === 'register' && !agreed && !requestConsent) ||
          (proof && code.length !== 8)
        }
      >
        {busy
          ? t('Please wait…', 'Aguarde…')
          : mode === 'login'
            ? t('Sign in', 'Entrar')
            : mode === 'register' || mode === 'forgot'
              ? mode === 'register'
                ? t('Create account', 'Criar conta')
                : t('Send recovery email', 'Enviar e-mail de recuperação')
              : t('Confirm', 'Confirmar')}
      </Button>
      {mode === 'login' && (
        <Button
          type="button"
          disabled={busy || !email.trim() || !password}
          onClick={() => {
            void submit(false, 'resend');
          }}
        >
          {t('Resend verification email', 'Reenviar confirmação de e-mail')}
        </Button>
      )}
      {mode === 'login' && (
        <Button
          type="button"
          disabled={busy}
          onClick={() => {
            switchMode('forgot');
          }}
        >
          {t('Forgot password?', 'Esqueci minha senha')}
        </Button>
      )}
      {mode !== 'login' && (
        <Button
          type="button"
          disabled={busy}
          onClick={() => {
            switchMode('login');
          }}
        >
          {t('Back to email sign-in', 'Voltar ao login com e-mail')}
        </Button>
      )}
    </form>
  );
}
